import GearApi from '@/api/GearApi';
import JustificatoryApi from '@/api/JustificatoryApi';
import LanguageApi from '@/api/LanguageApi';
import OrderApiAgency from '@/api/OrderApiAgency';
import OrderApiClient from '@/api/OrderApiClient';
import OrderCancelRequestApi from '@/api/OrderCancelRequestApi';
import OrderInterlocutorApiAgency from '@/api/OrderInterlocutorApiAgency';
import OrderUserApiAgency from '@/api/OrderUserApiAgency';
import OrderUserApiClient from '@/api/OrderUserApiClient';
import ProfessionalSkillApi from '@/api/ProfessionalSkillApi';
import ScheduleDefaultApiAgency from '@/api/ScheduleDefaultApiAgency';
import type OrderEntity from '@/entity/order/OrderEntity';
import type OrderTempWorkerEntity from '@/entity/order/OrderTempWorkerEntity';

import { i18n } from '@/i18n';
import router from '@/router/index.router';
import { DOCUMENT_TYPE } from '@/typings/enums/DOCUMENT_TYPE';
import { ORDER_STATUS } from '@/typings/enums/ORDER_STATUS';
import { isSpecific } from '@/utils/tools';
import { Bus, getHoursInterval, itemIs, useAside, useConfirm, useModal, useNotif, usePrompt } from '@orion.ui/orion';
import { defineAsyncComponent, ref } from 'vue';
import BaseService from '../BaseService';
import useBusService from '../BusService';

type Order = IOrderTempWorkerOverPageModel | IOrderAgencyOverPageModel | IOrderClientOverPageModel;

class OrderService extends BaseService {

	getRichStatus (status: ORDER_STATUS) {
		switch (status) {
			case ORDER_STATUS.Created:
				return {
					color: 'info' as Orion.Color,
					outline: true,
					label: i18n.global.t('common.status.inProgress'),
					status,
				};
			case ORDER_STATUS.Filled:
				return {
					color: 'success' as Orion.Color,
					outline: true,
					label: i18n.global.t('common.status.filled'),
					status,
				};
			case ORDER_STATUS.Canceled:
				return {
					color: 'danger' as Orion.Color,
					outline: true,
					label: i18n.global.t('common.cancelled'),
					status,
				};
			case ORDER_STATUS.Validated:
				return {
					color: 'success' as Orion.Color,
					outline: true,
					label: i18n.global.t('business.order.validated'),
					status,
				};
			case ORDER_STATUS.CancellationRequest:
				return {
					color: 'warning' as Orion.Color,
					outline: true,
					label: i18n.global.t('common.status.cancellationRequest'),
					status,
				};
			default:
				return undefined;
		}
	}

	isOrderProfessionalSkill (order: OrderEntity, skillId: number) {
		return skillId === order.professionalSkill?.id;
	}

	canCancelOrder (order: IOrderAgencyListItemModel | IOrderAgencyOverPageModel) {
		return this.self.isAgency()
		  && order.status !== ORDER_STATUS.Canceled
		  && order.status !== ORDER_STATUS.Validated;
	}

	getOrderHoursInterval (start: string, end: string) {
		return getHoursInterval(new Date(start), new Date(end));
	}

	getOrderDiscussionContext (order: Order, candidate?: Armado.OrderDiscussionContextCandidate) {
		const context: Armado.DiscussionContext = {
			id: 0,
			documentId: order.id,
			documentType: DOCUMENT_TYPE.Order,
			title: this.self.isTempWorker() || candidate
				? i18n.global.t('business.missionOfferNumber', { id: order.id })
				: i18n.global.t('admin.order.dashboardHome.orderNumber', { id: order.id }),
			subtitle: i18n.global.t('business.missionFromTo'),
		};

		if (this.self.isTempWorker() && itemIs<IOrderTempWorkerOverPageModel>(order, 'agencyCompany')) {
			Object.assign(context, {
				agencyCompanyId: order.agencyCompany?.id,
				agencyCompany: {
					id: order.agencyCompany?.id,
					name: order.agencyCompany?.name,
				},
				tempWorkerUser: {
					id: this.self.id,
					name: this.self.name,
					avatarId: this.self.avatarId,
				},
				// title: i18n.global.t('business.missionOfferNumber'),
			});
		}
		else if (itemIs<IOrderAgencyOverPageModel | IOrderClientOverPageModel>(order, 'agencyCompanyId')) {
			Object.assign(context, {
				agencyCompanyId: order.agencyCompanyId,
				agencyCompany: {
					id: order.agencyCompanyId,
					name: order.agencyCompanyName,
				},
			});

			if (candidate) {
				Object.assign(context, {
					tempWorkerUserId: candidate.userId,
					tempWorkerUser: {
						id: candidate.userId,
						name: candidate.userName,
						avatarId: candidate.userAvatarId,
					},
					// title: i18n.global.t('business.missionOfferNumber'),
				});
			}
			else {
				Object.assign(context, {
					clientCompanyId: order.clientCompanyId,
					clientCompany: {
						id: order.clientCompanyId,
						name: order.clientCompanyName,
					},
					// title: `Commande n°${order.id}`,
				});
			}
		}

		return context;
	}

	async openAsideCopyOrderAsync (order?: OrderEntity) {
		if (!this.self.can('OrderCreate')) return;
		await order?.filterSelfTagsAsync();

		const clone = order?.clone();
		clone?.afterCloneHook();

		useAside({
			Nested: defineAsyncComponent(() => import('@/components/order/creation/OrderCreationAside.vue')),
			size: 'lg',
			NestedProps: { orderCopy: clone },
			events:
			{
				save: (Aside, order) => {
					useNotif.success(i18n.global.t('business.orderCreatedSuccessfully'));
					Bus.emit('OrderPagedListViewSetup:refreshList', order);
				},
			},
		});

	}

	async validateOrderAsync (id: number) {
		if (await useConfirm(i18n.global.t('business.order.confirmValidation'))) {
			await OrderApiAgency.validate(id);
			useNotif.success(i18n.global.t('business.orderValidatedSuccessfully'));
			return true;
		}
		else return false;
	}

	async cancelOrderAsync (id: number) {
		if (this.self.isClient()) {
			const message = ref('');
			const { confirm, value } = await usePrompt({
				size: 'md',
				title: i18n.global.t('business.order.confirmCancellation'),
				message: i18n.global.t('business.commentWillBeVisibleByAgency'),
				prompt: {
					type: 'textarea',
					value: message.value,
					fieldProps: {
						maxLength: 500,
						required: true,
					},
				},
			});

			if (confirm) {
				await OrderCancelRequestApi.add({
					orderId: id,
					reason: value as string,
					creatorId: this.self.userId,
				});

				useBusService().emit(`${id}:statusUpdate`, ORDER_STATUS.CancellationRequest);
				useBusService().emit(`${id}:detailsStatusUpdate`, {
					status: ORDER_STATUS.CancellationRequest,
					reason: value,
				});
				Bus.emit('OrderPagedListViewSetup:refreshList');
				useNotif.success(i18n.global.t('business.cancellationRequestSent'));
			}
		}
		else {
			if (await useConfirm(i18n.global.t('business.order.confirmCancellation'))) {
				await OrderApiAgency.canceled(id);
				useBusService().emit(`${id}:statusUpdate`, ORDER_STATUS.Canceled);
				useBusService().emit(`${id}:detailsStatusUpdate`, { status: ORDER_STATUS.Canceled });
				Bus.emit('OrderPagedListViewSetup:refreshList');
				useNotif.success(i18n.global.t('business.orderCancelledSuccessfully'));
				router.push({ name: 'Order' });
			}
		}

	}

	async deleteCancelOrderRequestAsync (id: number) {
		if (await useConfirm(
			i18n.global.t('business.doYouReallyWantToWithdrawRequest'),
			{ title: i18n.global.t('common.label.cancellation') },
		)) {
			await OrderCancelRequestApi.delete(id);

			useBusService().emit(`${id}:statusUpdate`, ORDER_STATUS.Created);
			useBusService().emit(`${id}:detailsStatusUpdate`, { status: ORDER_STATUS.Created });
			Bus.emit('OrderPagedListViewSetup:refreshList');
			useNotif.success(i18n.global.t('business.requestCancelled'));
		}
	}

	async getCountAsync () {
		const api = this.self.isAgency() ? OrderApiAgency : OrderApiClient;
		const { data } = await api.gridCount();

		return {
			inProgress: data.inProgress,
			filled: data.filled,
			validated: data.validated,
			canceled: data.canceled,
			cancellationRequest: data.cancellationRequest,
		};
	}

	async addOrderAsync (order: IAddOrderForAgencyModel | IAddOrderModel) {
		const { data: orderCreated } = this.self.isAgency()
			? await OrderApiAgency.add(order as IAddOrderForAgencyModel)
			: await OrderApiClient.add(order);
		return orderCreated;
	}

	// ORDER PIPELINE FUNCTIONS
	async removeCandidateFromOrderAsync (orderId: number, userIds: number[]) {
		await OrderUserApiAgency.deleteMultiple({
			userIds: userIds,
			orderId,
		});
	}

	async getValidatedCandidateListAsync (orderId: number, page: IOrderUserClientPagedListParameters) {
		const { data } = await OrderUserApiClient.listValidated(orderId, page);
		return data;
	}

	async getTempworkerDetailsAsync (...args: Parameters<typeof OrderUserApiClient.getDetails>) {
		const { data } = await OrderUserApiClient.getDetails(...args);
		return data;
	}

	async moveCandidateAsync (...args: Parameters<typeof OrderUserApiAgency.move>) {
		if (!this.self.isAgency()) return;
		const { data } = await OrderUserApiAgency.move(...args);
		return data;
	}

	// ORDER UPDATE FUNCTIONS
	async updateAsync (...args: Parameters<typeof OrderApiAgency.update>) {
		if (!this.self.isAgency()) return;
		const { data } = await OrderApiAgency.update(...args);
		return data;
	}

	async updateNeedPersonalVehicleAsync (orderId: number, needVehicle: IUpdateOrderNeedPersonalVehicleModel) {
		await OrderApiAgency.updateNeedPersonalVehicle(orderId, needVehicle);
		useNotif.success(i18n.global.t('common.modificationSaved'));
	}

	async updateProfessionalSkillAsync (orderId: number, payload: IUpdateOrderProfessionalSkillModel) {
		await OrderApiAgency.updateProfessionalSkill(orderId, payload);
	}

	async updateSecondaryProfessionalSkillAsync (orderId: number, payload: IUpdateOrderSecondaryProfessionalSkillModel) {
		await OrderApiAgency.updateSecondaryProfessionalSkill(orderId, payload);
	}

	async addLanguageAsync (orderId: number, payload: IAddOrderLanguageModel) {
		await OrderApiAgency.addLanguage(orderId, payload);
	}

	async addDocumentAsync (orderId: number, payload: IAddOrderJustificatoryModel) {
		await OrderApiAgency.addJustificatory(orderId, payload);
	}

	async removeDocumentAsync (orderId: number, id: number) {
		await OrderApiAgency.deleteJustificatory(orderId, id);
	}

	async addGearAsync (orderId: number, payload: IAddOrderGearModel) {
		await OrderApiAgency.addGear(orderId, payload);
	}

	async updateWarnClientAsync (orderId: number) {
		await OrderApiAgency.updateWarnClient(orderId);
	}

	async removeGearAsync (orderId: number, id: number) {
		await OrderApiAgency.deleteGear(orderId, id);
	}

	async updateInterlocutorAsync (orderId: number, payload: IUpdateOrderInterlocutorModel) {
		await OrderInterlocutorApiAgency.update(orderId, payload);
	}

	async deleteInterlocutorAsync (orderId: number) {
		await OrderInterlocutorApiAgency.delete(orderId);
	}

	async updateScheduleDefaultAsync (orderId: number, payload: IAddScheduleDefaultModel[]) {
		await ScheduleDefaultApiAgency.update({
			orderId,
			scheduleDefaultModels: payload,
		});
	}

	async openModifyModalAsync (order: OrderEntity, type: string, size = 'md') {
		useModal({
			Nested: defineAsyncComponent(() => import('@/components/order/OrderModifyModal.vue')),
			title: i18n.global.t('business.orderModification'),
			NestedProps: {
				tags: type,
				order: order.clone(),
			},
			size,
			events:
				{
					'updateOrder': async (Modal, payload) => {
						order.applyClone();
						await order.updateAsync(payload);
						useNotif.success(i18n.global.t('common.modificationSaved'));
						Modal.close();
					},
					'updateProfessionalSkill': async (Modal, payload) => {
						await order.updateProfessionalSkillAsync({ professionalSkillId: payload.id });
						order.applyClone();
						Modal.close();
						useNotif.success(i18n.global.t('business.qualificationModifiedSuccessfully'));
					},
					'updateSecondaryProfessionalSkill': async (Modal, payload: IProfessionalSkillModel[]) => {
						order.applyClone();
						const newProfessionalSkillsList = payload ? payload.map(x => x.id) : [];
						await order.updateSecondaryProfessionalSkillAsync({ secondaryProfessionalSkillIds: newProfessionalSkillsList });
						Modal.close();
						useNotif.success(i18n.global.t('business.secondaryQualificationModifiedSuccessfully'));
					},
					'updateLanguages': async (Modal, payload: ILanguageModel[]) => {
						order.applyClone();
						const newLanguagesIdList = payload ? payload.map(x => x.id) : [];
						await order.addLanguageAsync(newLanguagesIdList);
						Modal.close();
						useNotif.success(i18n.global.t('business.languagesModifiedSuccessfully'));
					},
					'updateInterlocutor': async (Modal, payload: IOrderInterlocutorModel) => {
						order.applyClone();
						await order.updateInterlocutorAsync(payload);
						Modal.close();
						useNotif.success(i18n.global.t('business.interlocutorModifiedSuccessfully'));
					},
					'deleteInterlocutor': async (Modal) => {
						await order.deleteInterlocutorAsync();
						Modal.close();
						order.orderInterlocutor = undefined;
						useNotif.success(i18n.global.t('business.interlocutorDeleted'));
					},
					'updateScheduleDefault': async (Modal, payload: IScheduleDefaultModel[]) => {
						order.applyClone();
						order.scheduleDefaults = payload;
						await order.updateScheduleDefaultAsync(payload);
						useNotif.success(i18n.global.t('business.defaultScheduleModifiedSuccessfully'));
						Modal.close();
					},
				},
		});
	}

	async openNoteModalAsync (order: OrderEntity) {
		const clone = order.clone();
		const { confirm, value } = await usePrompt({
			title: i18n.global.t('common.label.annotations'),
			prompt: {
				type: 'textarea',
				value: clone.agencyNote,
			},
		});

		if (confirm && value !== order.agencyNote) {
			await OrderApiAgency.updateNote(order.id, { note: value as string });
			order.agencyNote = value as string;
		}
	}

	async openScheduleModalAsync (order?: OrderEntity | OrderTempWorkerEntity) {
		useModal({
			Nested: defineAsyncComponent(() => import('@/components/order/details/OrderDetailsSpecificScheduleModal.vue')),
			NestedProps: { order },
			title: i18n.global.t('business.missionSpecificSchedule'),
			size: 'lg',
			events: {
				'updated': async (modal, payload: IScheduleOrderRangeModel[]) => {
					if (!order || order.isTempWorkerOrder()) return;
					order.applyClone();
					order.scheduleOrderRanges = payload;
					await order.updateScheduleOrderRangeAsync(payload);
					useNotif.success(i18n.global.t('business.specificScheduleChangesSaved'));
					modal.close();
				},
			},
		});
	}

	async openScheduleRecapModalAsync (order?: OrderEntity | OrderTempWorkerEntity) {
		useModal({
			Nested: defineAsyncComponent(() => import('@/components/order/creation/OrderCreationScheduleRecap.vue')),
			NestedProps: { order },
			size: 'lg',
		});
	}

	// ORDER GET APIS
	async getJustificatoriesAsync () {
		const { data } = await JustificatoryApi.list();
		return data.filter(x => !isSpecific(x.tempWorkerDocumentType));
	}

	async getQualificationsAsync () {
		const { data } = await ProfessionalSkillApi.list();
		return data;
	}

	async getLanguagesAsync () {
		const { data } = await LanguageApi.list();
		return data;
	}

	async getGearsAsync () {
		const { data } = await GearApi.list();
		return data;
	}

	async getStepAsync (...args: Parameters<typeof OrderApiAgency.step>) {
		if (this.self.isAgency()) {
			const { data } = await OrderApiAgency.step(...args);
			return data;
		}
		return [];
	}

	async getDetailedAsync (id: number): Promise<IOrderAgencyOverPageModel | IOrderClientOverPageModel | undefined> {
		if (this.self.isAgency()) {
			const { data } = await OrderApiAgency.getDetailed(id);
			return data;
		}
		else if (this.self.isClient()) {
			const { data } = await OrderApiClient.getDetailed(id);
			return data;
		}
	}

	async overviewCountAsync (...args: Parameters<typeof OrderApiAgency.overviewCount>) {
		if (this.self.isAgency()) {
			const { data } = await OrderApiAgency.overviewCount(...args);
			return data;
		};
	}

	async averageCompletionAsync (...args: Parameters<typeof OrderApiAgency.averageCompletion>) {
		if (this.self.isAgency()) {
			const { data } = await OrderApiAgency.averageCompletion(...args);
			return data;
		};
	}

	async averageTimeCompletionAsync (...args: Parameters<typeof OrderApiAgency.averageTimeCompletion>) {
		if (this.self.isAgency()) {
			const { data } = await OrderApiAgency.averageTimeCompletion(...args);
			return data;
		};
	}

	async listCandidateStatusAsync (...args: Parameters<typeof OrderUserApiAgency.listCandidateStatus>) {
		if (this.self.isAgency()) {
			const { data } = await OrderUserApiAgency.listCandidateStatus(...args);
			return data;
		};
	}

}

const orderServiceSingleton = new OrderService();

export default function useOrderService () {
	return orderServiceSingleton;
}
